import { useEffect, useMemo, useState } from "react";
import { Search, ListChecks, Copy, AlertTriangle } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { useI18n } from "../../i18n";
import { fmtTime, isLocked } from "./utils";
import PointsBadge from "./PointsBadge";

/* Read-only row — every prediction the user has in the selected pool.
   No inline editing here — that happens on Fixtures + Match Detail pages. */
function PickedRow({ match, prediction, teams, predictedSlot, t, lang }) {
    const locked = isLocked(match.kickoff);
    const finished = match.status === "finished" && match.home_goals != null;
    const home = teams[match.home_team_id];
    const away = teams[match.away_team_id];
    const ps = predictedSlot || {};
    const homePredicted = !home && ps.home_team_id ? teams[ps.home_team_id] : null;
    const awayPredicted = !away && ps.away_team_id ? teams[ps.away_team_id] : null;
    return (
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)]" data-testid={`mypred-${match.id}`}>
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-bold">{match.round} · {fmtTime(match.kickoff, lang)}</div>
                <div className="font-heading text-base font-bold uppercase tracking-tight truncate">
                    <span className={homePredicted ? "text-amber-400 italic" : "text-[var(--text-primary)]"}>
                        {homePredicted && <span className="mr-1" aria-hidden="true">💭</span>}
                        {home?.name || homePredicted?.name || "TBD"}
                    </span>
                    <span className="text-[var(--text-muted)] mx-1">vs</span>
                    <span className={awayPredicted ? "text-amber-400 italic" : "text-[var(--text-primary)]"}>
                        {awayPredicted && <span className="mr-1" aria-hidden="true">💭</span>}
                        {away?.name || awayPredicted?.name || "TBD"}
                    </span>
                </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
                <div className="text-right">
                    <div className="text-[9px] uppercase tracking-widest text-[var(--text-muted)] font-bold">{t("preds.label.pick")}</div>
                    <div className="font-heading font-extrabold text-[var(--text-primary)] tabular-nums text-lg">{prediction.home_goals}–{prediction.away_goals}</div>
                    {prediction.home_goals === prediction.away_goals && prediction.pen_winner_team_id && teams[prediction.pen_winner_team_id] && (
                        <div className="text-[9px] uppercase tracking-widest font-bold text-amber-400" data-testid={`mypred-pen-${match.id}`}>
                            {t("bracket.pen_winner")}: {teams[prediction.pen_winner_team_id].name}
                        </div>
                    )}
                </div>
                {finished && (
                    <div className="text-right">
                        <div className="text-[9px] uppercase tracking-widest text-[var(--text-muted)] font-bold">{t("preds.label.result")}</div>
                        <div className="font-heading font-extrabold text-[var(--accent-hex)] tabular-nums text-lg">{match.home_goals}–{match.away_goals}</div>
                    </div>
                )}
                {finished
                    ? <PointsBadge pred={prediction} match={match} />
                    : locked
                        ? <span className="text-[10px] uppercase tracking-widest text-[#FF3B30] font-bold">{t("preds.locked")}</span>
                        : <span className="text-[10px] uppercase tracking-widest text-[#00FF66] font-bold">{t("preds.open")}</span>}
            </div>
        </div>
    );
}

function CopyModal({ open, pools, sourceId, onClose, onConfirm, busy }) {
    const { t } = useI18n();
    const [destId, setDestId] = useState("");
    // Reset destination when modal re-opens. Modal stays mounted so we
    // can't rely on re-mount for re-init.
    useEffect(() => { if (open) setDestId(""); }, [open]);
    if (!open) return null;
    const dests = pools.filter((p) => p.id !== sourceId);
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="copy-preds-modal">
            <div className="bg-[var(--bg-card)] border border-[var(--border-base)] rounded-lg max-w-md w-full p-6">
                <div className="flex items-center gap-2 text-amber-400 mb-3">
                    <AlertTriangle className="h-5 w-5" />
                    <h3 className="font-heading text-lg font-extrabold uppercase tracking-tight">{t("preds.copy.title")}</h3>
                </div>
                <p className="text-sm text-[var(--text-secondary)] mb-4">{t("preds.copy.warning")}</p>
                <label className="block text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)] mb-1.5" htmlFor="copy-dest">
                    {t("preds.copy.dest_label")}
                </label>
                <select
                    id="copy-dest"
                    data-testid="copy-preds-dest"
                    value={destId}
                    onChange={(e) => setDestId(e.target.value)}
                    className="w-full bg-[var(--bg-base)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2 rounded-md text-sm focus:outline-none focus:border-[var(--accent-hex)] mb-4"
                >
                    <option value="">{t("preds.copy.choose_pool")}</option>
                    {dests.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <div className="flex items-center justify-end gap-2">
                    <button onClick={onClose} data-testid="copy-preds-cancel" disabled={busy}
                        className="px-3 py-2 rounded text-xs font-extrabold uppercase tracking-widest text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
                        {t("common.cancel")}
                    </button>
                    <button onClick={() => onConfirm(destId)} disabled={!destId || busy}
                        data-testid="copy-preds-confirm"
                        className="px-4 py-2 rounded text-xs font-extrabold uppercase tracking-widest bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                        {busy ? "…" : t("preds.copy.confirm")}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function MyPredictions({ teams, poolId }) {
    const { t, lang } = useI18n();
    const [pools, setPools] = useState([]);
    const [predictions, setPredictions] = useState([]);
    const [matches, setMatches] = useState([]);
    const [predictedBracket, setPredictedBracket] = useState({});
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [showCopy, setShowCopy] = useState(false);
    const [copyBusy, setCopyBusy] = useState(false);

    // Load the list of pools the user belongs to — only needed so the
    // Copy-to-another-pool modal can list valid destinations.
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/pools/mine`);
                setPools(data || []);
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || e.message);
            }
        })();
    }, []);

    // Refetch predictions + matches whenever the pool prop changes
    // (driven by the header quick-switcher in PoolDetail navigating to a
    // new /pools/{id} URL, which re-mounts this view with a fresh poolId).
    useEffect(() => {
        if (!poolId) return;
        setLoading(true);
        (async () => {
            try {
                const [pred, mtch, pb] = await Promise.all([
                    api.get(`/pools/${poolId}/predictions/mine`),
                    api.get(`/matches`),
                    api.get(`/pools/${poolId}/predicted-bracket`).catch(() => ({ data: {} })),
                ]);
                setPredictions(pred.data || []);
                setMatches(mtch.data || []);
                setPredictedBracket(pb.data || {});
            } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || e.message);
            } finally { setLoading(false); }
        })();
    }, [poolId]);

    const predByMatch = useMemo(() => {
        const map = {};
        predictions.forEach((p) => { map[p.match_id] = p; });
        return map;
    }, [predictions]);

    const rows = useMemo(() => {
        const list = matches
            .filter((m) => !!predByMatch[m.id])
            .map((m) => ({ match: m, prediction: predByMatch[m.id] }));
        list.sort((x, y) => (y.match.kickoff || "").localeCompare(x.match.kickoff || ""));
        if (!search) return list;
        const q = search.toLowerCase();
        return list.filter(({ match: m }) => {
            const h = teams[m.home_team_id]?.name?.toLowerCase() || "";
            const a = teams[m.away_team_id]?.name?.toLowerCase() || "";
            const r = m.round?.toLowerCase() || "";
            return h.includes(q) || a.includes(q) || r.includes(q);
        });
    }, [matches, predByMatch, search, teams]);

    const handleCopy = async (destId) => {
        if (!destId || !poolId) return;
        setCopyBusy(true);
        try {
            const { data } = await api.post(`/pools/${poolId}/predictions/copy-to/${destId}`);
            const { copied, overwritten, skipped_locked } = data;
            toast.success(t("preds.copy.result", { c: copied, o: overwritten, s: skipped_locked }));
            setShowCopy(false);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setCopyBusy(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="my-predictions-view">
            <div className="flex flex-wrap items-center gap-2 justify-end">
                {pools.length > 1 && (
                    <button
                        type="button"
                        onClick={() => setShowCopy(true)}
                        data-testid="preds-copy-btn"
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-extrabold uppercase tracking-widest rounded border border-amber-500/50 text-amber-400 hover:border-amber-500 hover:text-amber-300 transition-colors"
                    >
                        <Copy className="h-3.5 w-3.5" /> {t("preds.copy.button")}
                    </button>
                )}
            </div>

            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-muted)]" />
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder={t("preds.search_ph")}
                    data-testid="preds-search"
                    className="w-full bg-[var(--bg-card)] border border-[var(--border-base)] text-[var(--text-primary)] pl-9 pr-3 py-2.5 rounded-md focus:outline-none focus:border-[var(--accent-hex)]"
                />
            </div>

            <section data-testid="picked-section">
                <h3 className="font-heading text-xl font-bold uppercase tracking-tight text-[var(--text-primary)] mb-3 flex items-center gap-2">
                    <ListChecks className="h-5 w-5 text-[var(--accent-hex)]" />
                    {t("preds.picked.title")}
                </h3>
                {loading ? (
                    <div className="text-sm text-[var(--text-secondary)] italic">…</div>
                ) : rows.length === 0 ? (
                    <div className="text-sm text-[var(--text-secondary)] italic" data-testid="preds-empty">
                        {t("preds.empty_for_pool")}
                    </div>
                ) : (
                    <div className="space-y-2">
                        {rows.map(({ match, prediction }) => (
                            <PickedRow
                                key={match.id}
                                match={match}
                                prediction={prediction}
                                teams={teams}
                                predictedSlot={predictedBracket[match.id]}
                                t={t}
                                lang={lang}
                            />
                        ))}
                    </div>
                )}
            </section>

            <CopyModal
                open={showCopy}
                pools={pools}
                sourceId={poolId}
                onClose={() => setShowCopy(false)}
                onConfirm={handleCopy}
                busy={copyBusy}
            />
        </div>
    );
}
